import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

import schoolLogo from "./zpa.png";

/*
|--------------------------------------------------------------------------
| ZION PRAISE ACADEMY — FINANCIAL STATEMENT THEME
|--------------------------------------------------------------------------
*/

const COLORS = {
  navy: "#102A43",
  royal: "#1D4ED8",
  sky: "#EAF4FF",
  gold: "#D6A63B",
  green: "#087F5B",
  greenLight: "#E8F7F0",
  red: "#C53030",
  redLight: "#FFF0F0",
  text: "#243447",
  gray: "#64748B",
  muted: "#94A3B8",
  border: "#D8E1EC",
  white: "#FFFFFF",
  background: "#F5F8FC",
  alternate: "#F4F8FD",
};

/*
|--------------------------------------------------------------------------
| PDF STYLES
|--------------------------------------------------------------------------
*/

const styles = StyleSheet.create({
  page: {
    paddingTop: 24,
    paddingBottom: 42,
    paddingHorizontal: 28,
    fontSize: 8,
    fontFamily: "Helvetica",
    color: COLORS.text,
    backgroundColor: COLORS.white,
  },

  // School header
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingBottom: 11,
    borderBottomWidth: 2.5,
    borderBottomColor: COLORS.royal,
    marginBottom: 10,
  },

  logo: {
    width: 58,
    height: 58,
    objectFit: "contain",
    marginRight: 13,
  },

  headerContent: {
    flex: 1,
  },

  schoolTitle: {
    fontSize: 19,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
    marginBottom: 4,
  },

  documentTitle: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    color: COLORS.royal,
    letterSpacing: 1.1,
    marginBottom: 4,
  },

  headerSubtitle: {
    fontSize: 7.5,
    color: COLORS.gray,
  },

  officialBadge: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: COLORS.navy,
    borderRadius: 4,
    minWidth: 112,
  },

  badgeTitle: {
    color: COLORS.white,
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    marginBottom: 3,
  },

  badgeSubtitle: {
    color: "#DCE8F5",
    fontSize: 6.5,
  },

  // Metadata strip
  metadataStrip: {
    flexDirection: "row",
    backgroundColor: COLORS.background,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 4,
    paddingVertical: 8,
    paddingHorizontal: 10,
    marginBottom: 10,
  },

  metadataItem: {
    flex: 1,
    paddingHorizontal: 7,
    borderRightWidth: 1,
    borderRightColor: COLORS.border,
  },

  metadataItemLast: {
    flex: 1,
    paddingHorizontal: 7,
  },

  metadataLabel: {
    fontSize: 6.5,
    color: COLORS.gray,
    marginBottom: 4,
    textTransform: "uppercase",
  },

  metadataValue: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
  },

  // Main section titles
  sectionHeading: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
    marginTop: 3,
  },

  sectionTitle: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
  },

  sectionCaption: {
    fontSize: 7,
    color: COLORS.gray,
  },

  // Student and account overview
  overviewRow: {
    flexDirection: "row",
    alignItems: "stretch",
    marginBottom: 10,
  },

  studentPanel: {
    width: "49%",
    flexDirection: "row",
    alignItems: "center",
    padding: 10,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 4,
    marginRight: "2%",
    backgroundColor: COLORS.white,
  },

  studentImage: {
    width: 58,
    height: 58,
    objectFit: "cover",
    marginRight: 11,
    borderRadius: 3,
    borderWidth: 1,
    borderColor: COLORS.border,
  },

  imagePlaceholder: {
    width: 58,
    height: 58,
    marginRight: 11,
    borderRadius: 3,
    backgroundColor: COLORS.sky,
    alignItems: "center",
    justifyContent: "center",
  },

  placeholderText: {
    fontSize: 20,
    fontFamily: "Helvetica-Bold",
    color: COLORS.royal,
  },

  studentDetails: {
    flex: 1,
  },

  studentName: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
    marginBottom: 7,
  },

  studentInfoRow: {
    flexDirection: "row",
    marginBottom: 4,
  },

  studentLabel: {
    width: 82,
    fontSize: 7,
    color: COLORS.gray,
  },

  studentValue: {
    flex: 1,
    fontSize: 7.5,
    fontFamily: "Helvetica-Bold",
    color: COLORS.text,
  },

  // Financial summary cards
  summaryPanel: {
    width: "49%",
    padding: 9,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 4,
    backgroundColor: COLORS.white,
  },

  summaryHeading: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
    marginBottom: 7,
  },

  summaryCardsRow: {
    flexDirection: "row",
  },

  summaryMetric: {
    flex: 1,
    paddingVertical: 9,
    paddingHorizontal: 7,
    marginRight: 6,
    borderRadius: 4,
    backgroundColor: COLORS.background,
    borderTopWidth: 2,
    borderTopColor: COLORS.royal,
  },

  summaryMetricLast: {
    flex: 1,
    paddingVertical: 9,
    paddingHorizontal: 7,
    borderRadius: 4,
    backgroundColor: COLORS.background,
    borderTopWidth: 2,
    borderTopColor: COLORS.gold,
  },

  paidMetric: {
    borderTopColor: COLORS.green,
    backgroundColor: COLORS.greenLight,
  },

  balanceMetric: {
    borderTopColor: COLORS.red,
    backgroundColor: COLORS.redLight,
  },

  metricLabel: {
    fontSize: 6.5,
    color: COLORS.gray,
    marginBottom: 6,
  },

  metricValue: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
  },

  metricSubtext: {
    fontSize: 6.5,
    color: COLORS.gray,
    marginTop: 4,
  },

  // Transaction ledger
  ledgerSection: {
    marginTop: 2,
    marginBottom: 5,
  },

  ledgerHeadingRow: {
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
    paddingBottom: 6,
  },

  ledgerTitle: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
  },

  ledgerSubtitle: {
    fontSize: 7,
    color: COLORS.gray,
    marginTop: 3,
  },

  table: {
    width: "100%",
    borderWidth: 1,
    borderColor: COLORS.border,
  },

  tableHeader: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: COLORS.navy,
    minHeight: 25,
    paddingVertical: 7,
    paddingHorizontal: 5,
  },

  tableHeaderText: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    color: COLORS.white,
  },

  tableRow: {
    flexDirection: "row",
    alignItems: "center",
    minHeight: 23,
    paddingVertical: 6,
    paddingHorizontal: 5,
    borderBottomWidth: 0.6,
    borderBottomColor: COLORS.border,
  },

  tableRowAlternate: {
    backgroundColor: COLORS.alternate,
  },

  tableCell: {
    fontSize: 7.2,
    color: COLORS.text,
  },

  numberCell: {
    width: "5%",
    paddingRight: 3,
  },

  dateCell: {
    width: "12%",
    paddingRight: 4,
  },

  categoryCell: {
    width: "14%",
    paddingRight: 4,
  },

  descriptionCell: {
    width: "24%",
    paddingRight: 5,
  },

  paidCell: {
    width: "12%",
    textAlign: "right",
    paddingRight: 5,
  },

  balanceCell: {
    width: "14%",
    textAlign: "right",
    paddingRight: 5,
  },

  cashierCell: {
    width: "19%",
    paddingLeft: 5,
  },

  amountText: {
    fontFamily: "Helvetica-Bold",
    color: COLORS.green,
  },

  balanceText: {
    color: COLORS.red,
  },

  // Table ending and financial verification
  tableEnd: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: COLORS.sky,
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderTopWidth: 1,
    borderTopColor: COLORS.royal,
  },

  tableEndLabel: {
    fontSize: 7.5,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
  },

  tableEndValue: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
  },

  emptyState: {
    paddingVertical: 24,
    paddingHorizontal: 12,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: COLORS.border,
    backgroundColor: COLORS.background,
  },

  emptyTitle: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
    marginBottom: 5,
  },

  emptyText: {
    fontSize: 7.5,
    color: COLORS.gray,
  },

  // Closing statement and signatures
  closingSection: {
    marginTop: 11,
  },

  verificationBox: {
    padding: 9,
    backgroundColor: COLORS.background,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderLeftWidth: 3,
    borderLeftColor: COLORS.royal,
    borderRadius: 3,
  },

  verificationTitle: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
    marginBottom: 5,
  },

  verificationText: {
    fontSize: 7.5,
    lineHeight: 1.5,
    color: COLORS.gray,
  },

  signatureRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 23,
    paddingHorizontal: 8,
  },

  signatureBlock: {
    width: "29%",
    paddingTop: 5,
    borderTopWidth: 1,
    borderTopColor: COLORS.text,
  },

  signatureTitle: {
    fontSize: 7.5,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
  },

  signatureSubtitle: {
    fontSize: 6.5,
    color: COLORS.gray,
    marginTop: 3,
  },

  // Repeating page footer
  footer: {
    position: "absolute",
    left: 28,
    right: 28,
    bottom: 16,
    paddingTop: 6,
    borderTopWidth: 1,
    borderTopColor: COLORS.border,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },

  footerText: {
    fontSize: 6.5,
    color: COLORS.gray,
  },

  footerBrand: {
    fontSize: 6.5,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
  },
});

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const safeText = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (typeof value === "object") {
    return value.name || value.year || fallback;
  }

  return String(value);
};

const toNumber = (value) => {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
};

const formatMoney = (amount) =>
  toNumber(amount).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (date) => {
  if (!date) return "-";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return String(date);
  }

  return parsedDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getInitials = (name) => {
  const parts = safeText(name, "Student Name")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "ST";
};

/*
|--------------------------------------------------------------------------
| STUDENT PAYMENT HISTORY PDF
|--------------------------------------------------------------------------
*/

const StudentPaymentHistoryPDF = ({
  student,
  transactions = [],
  academicYear,
}) => {
  const records = Array.isArray(transactions) ? transactions : [];

  const totalPaid = records.reduce(
    (total, item) => total + toNumber(item.amount_paid),
    0
  );

  const totalBalance = records.reduce(
    (total, item) => total + toNumber(item.balance),
    0
  );

  const studentName = safeText(student?.full_name, "Student Name");
  const generatedDate = formatDate(new Date());
  const yearName = safeText(academicYear?.name || academicYear?.year);

  return (
    <Document
      title={`Student Payment Statement - ${studentName}`}
      author="Zion Praise Academy - CRM School System"
      subject="Official Student Financial Record"
    >
      <Page
        size="A4"
        orientation="landscape"
        wrap
        style={styles.page}
      >
        {/* SCHOOL HEADER */}

        <View style={styles.header}>
          <Image src={schoolLogo} style={styles.logo} />

          <View style={styles.headerContent}>
            <Text style={styles.schoolTitle}>
              ZION PRAISE ACADEMY
            </Text>

            <Text style={styles.documentTitle}>
              STUDENT PAYMENT STATEMENT
            </Text>

            <Text style={styles.headerSubtitle}>
              CRM School System  |  Finance & Student Accounts Department
            </Text>
          </View>

          <View style={styles.officialBadge}>
            <Text style={styles.badgeTitle}>OFFICIAL RECORD</Text>
            <Text style={styles.badgeSubtitle}>
              STUDENT FINANCE
            </Text>
          </View>
        </View>

        {/* REPORT METADATA */}

        <View style={styles.metadataStrip}>
          <View style={styles.metadataItem}>
            <Text style={styles.metadataLabel}>
              Academic Year
            </Text>
            <Text style={styles.metadataValue}>
              {yearName}
            </Text>
          </View>

          <View style={styles.metadataItem}>
            <Text style={styles.metadataLabel}>
              Statement Date
            </Text>
            <Text style={styles.metadataValue}>
              {generatedDate}
            </Text>
          </View>

          <View style={styles.metadataItem}>
            <Text style={styles.metadataLabel}>
              Transaction Records
            </Text>
            <Text style={styles.metadataValue}>
              {records.length}
            </Text>
          </View>

          <View style={styles.metadataItemLast}>
            <Text style={styles.metadataLabel}>
              Record Status
            </Text>
            <Text style={styles.metadataValue}>
              {records.length > 0 ? "Statement Generated" : "No Transactions"}
            </Text>
          </View>
        </View>

        {/* STUDENT PROFILE AND ACCOUNT SUMMARY */}

        <View style={styles.overviewRow}>
          <View style={styles.studentPanel}>
            {student?.student_image ? (
              <Image
                src={student.student_image}
                style={styles.studentImage}
              />
            ) : (
              <View style={styles.imagePlaceholder}>
                <Text style={styles.placeholderText}>
                  {getInitials(studentName)}
                </Text>
              </View>
            )}

            <View style={styles.studentDetails}>
              <Text style={styles.studentName}>
                {studentName}
              </Text>

              <View style={styles.studentInfoRow}>
                <Text style={styles.studentLabel}>
                  Admission No.
                </Text>
                <Text style={styles.studentValue}>
                  {safeText(student?.id)}
                </Text>
              </View>

              <View style={styles.studentInfoRow}>
                <Text style={styles.studentLabel}>
                  Grade / Class
                </Text>
                <Text style={styles.studentValue}>
                  {safeText(student?.grade_class_name)}
                </Text>
              </View>

              <View style={styles.studentInfoRow}>
                <Text style={styles.studentLabel}>
                  Academic Year
                </Text>
                <Text style={styles.studentValue}>
                  {yearName}
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.summaryPanel}>
            <Text style={styles.summaryHeading}>
              ACCOUNT FINANCIAL OVERVIEW
            </Text>

            <View style={styles.summaryCardsRow}>
              <View style={styles.summaryMetric}>
                <Text style={styles.metricLabel}>
                  TOTAL TRANSACTIONS
                </Text>
                <Text style={styles.metricValue}>
                  {records.length}
                </Text>
                <Text style={styles.metricSubtext}>
                  Recorded payments
                </Text>
              </View>

              <View
                style={[
                  styles.summaryMetric,
                  styles.paidMetric,
                ]}
              >
                <Text style={styles.metricLabel}>
                  TOTAL PAID
                </Text>
                <Text
                  style={[
                    styles.metricValue,
                    { color: COLORS.green },
                  ]}
                >
                  ${formatMoney(totalPaid)}
                </Text>
                <Text style={styles.metricSubtext}>
                  Payments recorded
                </Text>
              </View>

              <View
                style={[
                  styles.summaryMetricLast,
                  styles.balanceMetric,
                ]}
              >
                <Text style={styles.metricLabel}>
                  OUTSTANDING BALANCE
                </Text>
                <Text
                  style={[
                    styles.metricValue,
                    { color: COLORS.red },
                  ]}
                >
                  ${formatMoney(totalBalance)}
                </Text>
                <Text style={styles.metricSubtext}>
                  Balance reported
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* PAYMENT LEDGER */}

        <View style={styles.ledgerSection}>
          <View style={styles.ledgerHeadingRow}>
            <View>
              <Text style={styles.ledgerTitle}>
                PAYMENT TRANSACTION LEDGER
              </Text>
              <Text style={styles.ledgerSubtitle}>
                Detailed record of payments associated with this student
              </Text>
            </View>

            <Text style={styles.sectionCaption}>
              Currency: USD ($)
            </Text>
          </View>

          {records.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyTitle}>
                No Payment Transactions Found
              </Text>
              <Text style={styles.emptyText}>
                No transaction records were supplied for this student.
              </Text>
            </View>
          ) : (
            <View style={styles.table}>
              {/* Repeats automatically when the table spans pages */}

              <View
                style={styles.tableHeader}
                fixed
                wrap={false}
              >
                <Text
                  style={[
                    styles.tableHeaderText,
                    styles.numberCell,
                  ]}
                >
                  No.
                </Text>

                <Text
                  style={[
                    styles.tableHeaderText,
                    styles.dateCell,
                  ]}
                >
                  Date
                </Text>

                <Text
                  style={[
                    styles.tableHeaderText,
                    styles.categoryCell,
                  ]}
                >
                  Category
                </Text>

                <Text
                  style={[
                    styles.tableHeaderText,
                    styles.descriptionCell,
                  ]}
                >
                  Fee / Description
                </Text>

                <Text
                  style={[
                    styles.tableHeaderText,
                    styles.paidCell,
                  ]}
                >
                  Amount Paid
                </Text>

                <Text
                  style={[
                    styles.tableHeaderText,
                    styles.balanceCell,
                  ]}
                >
                  Balance
                </Text>

                <Text
                  style={[
                    styles.tableHeaderText,
                    styles.cashierCell,
                  ]}
                >
                  Cashier
                </Text>
              </View>

              {records.map((item, index) => (
                <View
                  key={item.id ?? `transaction-${index}`}
                  wrap={false}
                  style={[
                    styles.tableRow,
                    index % 2 === 1
                      ? styles.tableRowAlternate
                      : {},
                  ]}
                >
                  <Text
                    style={[
                      styles.tableCell,
                      styles.numberCell,
                    ]}
                  >
                    {index + 1}
                  </Text>

                  <Text
                    style={[
                      styles.tableCell,
                      styles.dateCell,
                    ]}
                  >
                    {formatDate(item.created_at)}
                  </Text>

                  <Text
                    style={[
                      styles.tableCell,
                      styles.categoryCell,
                    ]}
                  >
                    {safeText(item.payment_category, "Uncategorized")}
                  </Text>

                  <Text
                    style={[
                      styles.tableCell,
                      styles.descriptionCell,
                    ]}
                  >
                    {safeText(item.fee_type)}
                  </Text>

                  <Text
                    style={[
                      styles.tableCell,
                      styles.paidCell,
                      styles.amountText,
                    ]}
                  >
                    ${formatMoney(item.amount_paid)}
                  </Text>

                  <Text
                    style={[
                      styles.tableCell,
                      styles.balanceCell,
                      styles.balanceText,
                    ]}
                  >
                    ${formatMoney(item.balance)}
                  </Text>

                  <Text
                    style={[
                      styles.tableCell,
                      styles.cashierCell,
                    ]}
                  >
                    {safeText(item.created_by)}
                  </Text>
                </View>
              ))}

              {/* Ledger totals */}

              <View
                style={styles.tableEnd}
                wrap={false}
              >
                <Text style={styles.tableEndLabel}>
                  STATEMENT TOTALS  |  {records.length} TRANSACTION
                  {records.length === 1 ? "" : "S"}
                </Text>

                <View style={{ flexDirection: "row" }}>
                  <Text
                    style={[
                      styles.tableEndValue,
                      { marginRight: 24 },
                    ]}
                  >
                    Paid: ${formatMoney(totalPaid)}
                  </Text>

                  <Text
                    style={[
                      styles.tableEndValue,
                      { color: COLORS.red },
                    ]}
                  >
                    Balance: ${formatMoney(totalBalance)}
                  </Text>
                </View>
              </View>
            </View>
          )}
        </View>

        {/* VERIFICATION AND SIGNATURES */}

        <View style={styles.closingSection} wrap={false}>
          <View style={styles.verificationBox}>
            <Text style={styles.verificationTitle}>
              FINANCIAL RECORD VERIFICATION
            </Text>

            <Text style={styles.verificationText}>
              This statement presents the student payment transactions
              and financial totals supplied to the report. Please verify
              the entries against the school's official finance records.
              Any discrepancy should be referred to the Finance & Student
              Accounts Office for review.
            </Text>
          </View>

          <View style={styles.signatureRow}>
            <View style={styles.signatureBlock}>
              <Text style={styles.signatureTitle}>
                Prepared By
              </Text>
              <Text style={styles.signatureSubtitle}>
                Finance Officer
              </Text>
            </View>

            <View style={styles.signatureBlock}>
              <Text style={styles.signatureTitle}>
                Verified By
              </Text>
              <Text style={styles.signatureSubtitle}>
                Accounts Supervisor
              </Text>
            </View>

            <View style={styles.signatureBlock}>
              <Text style={styles.signatureTitle}>
                Authorized By
              </Text>
              <Text style={styles.signatureSubtitle}>
                School Administration
              </Text>
            </View>
          </View>
        </View>

        {/* REPEATING FOOTER */}

        <View style={styles.footer} fixed>
          <Text style={styles.footerBrand}>
            ZION PRAISE ACADEMY
          </Text>

          <Text style={styles.footerText}>
            Official Student Financial Statement
          </Text>

          <Text
            style={styles.footerText}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
};

export default StudentPaymentHistoryPDF;